import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AppLoader from '../components/AppLoader';
import fetchWithRefreshV2 from '../api/fetchWithRefreshV2';
import { ABHA_LINK_COMPLETE } from './routes';
import { track } from '../tracking/mixpanel';
import { DOCS_APP_BE_URL } from '../api/constants';

function generateDateString(val: string) {
    return `${new Date(val)?.toLocaleString('default', {
        weekday: 'short',
    })}, ${new Date(val).getDate()} ${new Date(val).toLocaleString('default', {
        month: 'short',
    })}`;
}

interface IAPIData {
    success: boolean;
    doctor: {
        doctor_name: {
            f: string;
            l: string;
        };
        doctor_spec: string;
    };
    patient: {
        patient_name: string;
        patient_mobile: string;
    };
    visit_count: number;
    last_visit: string;
    txn_id: string;
}

export default function Abha() {
    const [apidata, setApidata] = useState<IAPIData>();
    const [apiState, setApiState] = useState<'loading' | 'success' | 'error'>('loading');
    const [isOtpLoading, setIsOtpLoading] = useState<boolean>(false);

    const params: { hash: string } = useParams();
    const history = useHistory();
    const [otp, setOtp] = useState<string>('');

    const url = new URL(window.location.href);
    const forwardUrl = url.searchParams.get('next');
    const visitid = url.searchParams.get('visitid');
    const utm_source = url.searchParams.get('utm_source');
    const utm_medium = url.searchParams.get('utm_medium');
    const utm_campaign = url.searchParams.get('utm_campaign');

    useEffect(() => {
        setApiState('loading');
        fetchWithRefreshV2(
            `${DOCS_APP_BE_URL}/service/abha/get-otp-pt-details/?hash=${params.hash}&visitid=${visitid}`,
        )
            .then((res) => res.json())
            .then((data) => {
                if (data.success !== true) {
                    throw new Error(data?.error ?? 'Something went wrong');
                }
                track('ABHA_PT_DETAILS_LOADED', { hash: params.hash });
                setApiState('success');
                setApidata(data);
            })
            .catch((e: any) => {
                track('ABHA_PT_DETAILS_ERROR', { hash: params.hash, message: e.message ?? '' });
                setApiState('error');
                alert('Something went wrong');
            });
    }, []);

    function onSubmit() {
        if (otp && otp.length === 6 && isOtpLoading === false) {
            setIsOtpLoading(true);
            fetchWithRefreshV2(
                `${DOCS_APP_BE_URL}/service/abha/confirm-otp/?hash=${params.hash}&visitid=${visitid}`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        otp,
                        txn_id: apidata?.txn_id ?? '',
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )
                .then((res) => res.json())
                .then(
                    (data: {
                        health_id: string;
                        success: boolean;
                        error?: string;
                        magic_link?: string;
                    }) => {
                        if (data.success !== true) {
                            throw new Error(data?.error ?? 'Something went wrong');
                        }
                        track('ABHA_SUBMIT_SUCCESS', {
                            hash: params.hash,
                            visitid: visitid || undefined,
                            eutm_source: utm_source || undefined,
                            eutm_medium: utm_medium || undefined,
                            eutm_campaign: utm_campaign || undefined,
                        });

                        setIsOtpLoading(false);
                        history.push({
                            pathname: ABHA_LINK_COMPLETE(
                                data['health_id'],
                                forwardUrl || undefined,
                                visitid || undefined,
                                data.magic_link,
                            ),
                        });
                    },
                )
                .catch((e: any) => {
                    track('ABHA_SUBMIT_ERROR', { hash: params.hash, message: e.message ?? '' });
                    setIsOtpLoading(false);
                    alert(e?.message ?? 'Some error occured');
                    if (forwardUrl) {
                        window.open(forwardUrl);
                    }
                });
        }
    }

    return apiState === 'error' ? (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
            }}
        >
            <p style={{ fontSize: 16, margin: 'auto' }}>Some Error Occured</p>
        </div>
    ) : apiState === 'loading' ? (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
            }}
        >
            <AppLoader />
        </div>
    ) : (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                background: '#F4F4F4',
                gap: 12,
                height: '100vh',
                overflow: 'scroll',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 24,
                    gap: 5,
                    background: 'white',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="52"
                        height="52"
                        fill="#7265E3"
                    >
                        <path d="M22,7a4.34,4.34,0,0,1-1.26,2.81L13.3,17.27a5.46,5.46,0,0,1-.64.53,4.33,4.33,0,0,1-6.72-4h0A1.45,1.45,0,1,1,8.36,15,2,2,0,0,0,11,16.1h0l.19-.08a2.59,2.59,0,0,0,.5-.37L15.33,12,11.67,8.29a2,2,0,0,0-2.83,0,1.91,1.91,0,0,0-.53.91,1.45,1.45,0,0,1-1.75,2.32,1.51,1.51,0,0,1-.57-1A4.32,4.32,0,0,1,13.3,6.69L17,10.33,19.1,8.22a2,2,0,0,0,.07-2.78,9.7,9.7,0,1,0-.67,13.7h0c.23-.21.45-.43.66-.65a2,2,0,0,0-.07-2.78l-1.55-1.55,1.63-1.63,1.55,1.55a4.31,4.31,0,0,1,.15,6,12,12,0,1,1-.8-17c.27.26.54.53.8.81A4.27,4.27,0,0,1,22,7Z"></path>
                    </svg>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            alignItems: 'center',
                        }}
                    >
                        <p style={{ fontSize: 14, fontWeight: 400 }}>An initiative by</p>
                        <img src="/abdm3.png" alt="abdm3" width={91} height={48} />
                    </div>
                </div>
                <div style={{ fontWeight: 400, fontSize: 15, width: '100%' }}>
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: 20,
                        }}
                    >
                        Hello {apidata?.patient?.patient_name ?? ''},
                    </span>{' '}
                    <br />{' '}
                    <span style={{ color: '#215FFF' }}>
                        Dr. {apidata?.doctor?.doctor_name.f ?? ''}
                    </span>{' '}
                    {visitid
                        ? 'has sent your prescription. Access it securely by entering ABHA OTP. Your prescription will be linked to your ABHA 🔒 for easy sharing and anytime access.'
                        : 'requests you to create ABHA to store your medical records securely & share them with any doctor or hospital easily'}
                </div>
                {apidata?.visit_count && apidata?.visit_count !== 0 ? (
                    <p style={{ fontSize: 13, width: '100%' }}>
                        {apidata?.visit_count} visits{' '}
                        {apidata?.last_visit && apidata?.last_visit.length !== 0
                            ? `(Last visited: ${generateDateString(apidata.last_visit)})`
                            : ''}
                    </p>
                ) : null}
            </div>
            <div
                style={{
                    padding: '16px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: 8,
                    background: 'white',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        gap: 4,
                        width: '100%',
                    }}
                >
                    <p style={{ fontWeight: 700, fontSize: 20 }}>Enter OTP to create ABHA</p>
                    <p style={{ fontWeight: 400, fontSize: 13, color: '#A3A3A3' }}>
                        You would have received the <span style={{ color: '#1A1A1A' }}>OTP</span> on{' '}
                        <span style={{ color: '#1A1A1A' }}>
                            {apidata?.patient?.patient_mobile ?? ''}
                        </span>{' '}
                        from{' '}
                        <span style={{ color: '#1A1A1A' }}>National Health Authority (India)</span>
                    </p>
                </div>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        style={{
                            borderRadius: '8px',
                            border: '1px solid #215FFF',
                            width: '75%',
                            padding: '6px 10px',
                            height: 45,
                        }}
                        autoFocus={true}
                        value={otp}
                        maxLength={6}
                        onChange={(e) => setOtp(e.target.value as string)}
                        placeholder="Enter OTP"
                    />
                    <div
                        style={{
                            color: 'white',
                            background:
                                (otp && otp.length < 6) || isOtpLoading === true
                                    ? '#D1D1D1'
                                    : '#215FFF',
                            padding: '4px 8px',
                            display: 'flex',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 11,
                            borderRadius: 8,
                            cursor: 'pointer',
                            alignSelf: 'stretch',
                            alignItems: 'center',
                            gap: 4,
                        }}
                        onClick={onSubmit}
                    >
                        {isOtpLoading && (
                            <svg
                                className="w-16 h-16 fill-white animate-spin"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        )}
                        <p style={{ fontSize: 14 }}>{visitid ? 'View Rx' : 'Submit'}</p>
                    </div>
                </div>
                <a
                    style={{
                        textDecoration: 'underline',
                        fontSize: 13,
                        width: '100%',
                    }}
                    onClick={() => {
                        track('ABHA_BULK_REFRESH', { hash: params.hash });
                        window.location.reload();
                    }}
                >
                    Resend OTP
                </a>
                <div
                    style={{
                        borderRadius: '8px',
                        background: '#F4F4F4',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                        gap: 24,
                        padding: '12px 0',
                        marginTop: 12,
                    }}
                >
                    <img src="/abdm3.png" alt="abdm3" width="68" height="36" />
                    <img src="/abdm1.png" alt="abdm1" width="39" height="38" />
                    <img src="/abdm2.png" alt="abdm2" width="52" height="38" />
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 24,
                    background: 'white',
                    gap: 8,
                }}
            >
                <p style={{ fontSize: 20, fontWeight: 700 }}>Why ABHA?</p>
                <p style={{ fontSize: 14, fontWeight: 400 }}>
                    ABHA ID is your personal health ID. It safely stores your medical records and
                    shares them with doctors upon your consent
                </p>
                <iframe
                    className="w-full h-200 rounded-4"
                    style={{
                        width: '100%',
                        height: 200,
                        borderRadius: 8,
                    }}
                    id="ytplayer"
                    src="https://youtube.com/embed/76L__Z28KFo?mute=1"
                    title="PM Modi Announces National Digital Health Mission: 'Health ID For Each Indian'"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
                <p style={{ fontSize: 16, fontWeight: 700 }}>
                    Watch PM Narendra Modi explain the benefits of ABHA
                </p>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    background: 'white',
                    width: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#D1EDE1',
                        padding: '8px 16px',
                        width: '100%',
                    }}
                >
                    <p
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#0F6440',
                        }}
                    >
                        JOIN THE HEALTH REVOLUTION
                    </p>
                </div>
                <div
                    style={{
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        <p
                            style={{
                                fontSize: 12,
                                fontWeight: 400,
                            }}
                        >
                            Indians creating ABHA
                        </p>
                        <p
                            style={{
                                fontSize: 16,
                                fontWeight: 700,
                            }}
                        >
                            60 Crores
                        </p>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        <p
                            style={{
                                fontSize: 12,
                                fontWeight: 400,
                            }}
                        >
                            Medical Records Linked
                        </p>
                        <p
                            style={{
                                fontSize: 16,
                                fontWeight: 700,
                            }}
                        >
                            200 Crores
                        </p>
                    </div>
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    background: 'white',
                    width: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#D1EDE1',
                        padding: '8px 16px',
                        width: '100%',
                    }}
                >
                    <p
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#0F6440',
                        }}
                    >
                        YOUR ABHA IS ACCEPTED AT
                    </p>
                </div>
                <div
                    style={{
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <p
                        style={{
                            fontSize: 16,
                            fontWeight: 700,
                        }}
                    >
                        45,000+ Health Facilities across India
                    </p>
                </div>
            </div>
            <div
                style={{
                    background: '#F4F4F4',
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    gap: 24,
                    padding: '8px 0 12px 0',
                }}
            >
                <img src="/abdm3.png" alt="abdm3" width="68" height="36" />
                <img src="/abdm1.png" alt="abdm1" width="39" height="38" />
                <img src="/abdm2.png" alt="abdm2" width="52" height="38" />
            </div>
        </div>
    );
}
